import React, { createContext, useContext, useState, useEffect } from 'react';
import config from './config';

const THEME_STORAGE_KEY = 'app_theme_settings';
const THEME_VERSION = 'sparkfinance_v1';

const initialSettings = {
  brand: {
    name: 'Spark Finance',
    tagline: 'Smart Lending, Simplified',
    logo: 'https://sparkfinance.com.au/wp-content/uploads/2024/02/spark-logo-1.svg',
    logoAlt: 'Spark Finance Logo',
    logoWidth: '200px',
    favicon: 'https://sparkfinance.com.au/wp-content/uploads/2024/02/cropped-spark-favicon-32x32.png',
  },
  colors: {
    primary: '#7ab648',
    primaryHover: '#6aa33d',
    primaryLight: '#f0f8e8',

    navbarBackground: '#ffffff',
    navbarText: '#2d2d2d',
    footerBackground: '#f7faf3',
    footerText: '#555555',

    background: '#f9fbf6',
    backgroundCard: '#ffffff',

    textPrimary: '#2d2d2d',
    textSecondary: '#666666',

    buttonPrimary: '#7ab648',
    buttonPrimaryHover: '#6aa33d',
    buttonText: '#ffffff',

    border: '#e2ead6',
    radius: '12',
    buttonRadius: '8',
  },
  text: {
    appTitle: 'Identity Verification',
  },
  liveness: {
    enabled: true,
  },
  membership: {
    enabled: false,
    providerName: 'Spark Finance',
    providerIcon: 'https://sparkfinance.com.au/wp-content/uploads/2024/02/spark-logo-1.svg',
    tier1Label: '1 Year',
    tier1Price: '10',
    tier2Label: '3 Years',
    tier2Price: '20',
    tier3Label: '5 Years',
    tier3Price: '35'
  }
};

const SettingsContext = createContext();

export const useSettings = () => useContext(SettingsContext);

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(() => {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    const version = localStorage.getItem('app_theme_version');
    if (saved && version === THEME_VERSION) return JSON.parse(saved);
    // Clear stale theme from previous branding
    localStorage.removeItem(THEME_STORAGE_KEY);
    localStorage.setItem('app_theme_version', THEME_VERSION);
    return initialSettings;
  });

  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(settings));

    const root = document.documentElement;

    // Apply colors to root variables using kebab-case
    Object.entries(settings.colors).forEach(([key, value]) => {
      const cssKey = key.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
      root.style.setProperty(`--color-${cssKey}`, value);
    });

    // Handle radii and custom variables
    root.style.setProperty('--border-radius-card', `${settings.colors.radius}px`);
    root.style.setProperty('--border-radius-button', `${settings.colors.buttonRadius}px`);
    root.style.setProperty('--logo-width', settings.brand.logoWidth);

    // Explicit overrides for the specific variable names used by the app layout
    root.style.setProperty('--color-navbar-bg', settings.colors.navbarBackground);
    root.style.setProperty('--color-footer-bg', settings.colors.footerBackground);

    // Dynamic body backgrounds
    root.style.setProperty('--color-background', settings.colors.background);
    document.body.style.background = settings.colors.background;

    // Favicon logic
    if (settings.brand.favicon) {
      let existingFavicon = document.querySelector("link[rel*='icon']");
      if (!existingFavicon) {
        existingFavicon = document.createElement('link');
        existingFavicon.rel = 'icon';
        document.head.appendChild(existingFavicon);
      }
      existingFavicon.href = settings.brand.favicon;
    }

    // Update document title
    if (settings.text.appTitle) {
      document.title = settings.text.appTitle;
    }

  }, [settings]);

  const updateSetting = (section, key, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value
      }
    }));
  };

  const resetToDefaults = () => {
    setSettings(initialSettings);
  };

  return (
    <SettingsContext.Provider value={{
      settings,
      updateSetting,
      resetToDefaults,
      isSettingsOpen,
      setIsSettingsOpen
    }}>
      {children}
    </SettingsContext.Provider>
  );
};
